/**
 * The only thing the setup checklist persists: that a team is done with the card. Progress is
 * derived per render and regresses, so completion has to be latched separately. Pure over a
 * raw string — `localStorage`, which throws in a blocked context, belongs to the caller.
 *
 * The `nexoya4agency.` prefix is the plan's pre-rename name and is frozen: renaming it to
 * `starter.` silently un-retires every team that already finished onboarding.
 */
export const CHECKLIST_STORAGE_KEY = 'nexoya4agency.checklist.v1';

/** `collapsed` is legacy: the rail card opens a popover now, and nothing persists that. */
export type ChecklistTeamState = { retiredAt?: string };
export type ChecklistState = Record<string, ChecklistTeamState>;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function parseChecklistState(raw: string | null): ChecklistState {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!isPlainObject(parsed)) return {};

  // Dropping non-object entries here keeps `ChecklistState` honest for every reader below.
  return Object.fromEntries(Object.entries(parsed).filter(([, value]) => isPlainObject(value))) as ChecklistState;
}

/** `teamId` is a number in `TeamProvider` and a string everywhere it round-trips a URL. */
const keyFor = (teamId: number | string): string => String(teamId);

export function isRetired(state: ChecklistState, teamId: number | string): boolean {
  return typeof state[keyFor(teamId)]?.retiredAt === 'string';
}

export function withRetired(state: ChecklistState, teamId: number | string, nowIso: string): ChecklistState {
  const key = keyFor(teamId);
  return { ...state, [key]: { ...state[key], retiredAt: nowIso } };
}

export function serializeChecklistState(state: ChecklistState): string {
  return JSON.stringify(state);
}
